import React from "react"
import {Paper, Box, Typography, Divider, Chip} from "@mui/material"

import {OfferTierCard} from "./OfferTierCard"
import {OfferActionsMenu} from "./OfferActionsMenu"

import {CustomerOffer} from "@indebted/api"

type OfferCardProps = {
	offer: CustomerOffer
	customerID: string
	onRevokeSuccess: () => void
}

function OfferCard({offer, customerID, onRevokeSuccess}: OfferCardProps) {
	const formatDate = (dateString: string) => {
		if (!dateString) return ""
		const date = new Date(dateString)
		return date.toLocaleDateString()
	}

	const sortedTiers = React.useMemo(
		() =>
			[...offer.Tiers].sort((a, b) => {
				if (a.TierType === "ONE_OFF" && b.TierType !== "ONE_OFF") return -1
				if (a.TierType !== "ONE_OFF" && b.TierType === "ONE_OFF") return 1
				return (a.MinimumNumberOfPayments ?? 0) - (b.MinimumNumberOfPayments ?? 0)
			}),
		[offer.Tiers],
	)

	return (
		<Paper
			variant="outlined"
			style={{
				padding: "16px",
				borderLeft: offer.Status.toLowerCase() === "active" ? "4px solid #4caf50" : "4px solid #e0e0e0",
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
				<Box>
					<Box display="flex" alignItems="flex-start" style={{gap: "8px"}} mb={1}>
						<Typography variant="h6">
							Offer available to customer from {formatDate(offer.StartDate)} to{" "}
							{formatDate(offer.EndDate)}
						</Typography>
						<Chip
							label={offer.Status.toLowerCase() === "active" ? "AVAILABLE" : offer.Status.toUpperCase()}
							size="small"
							style={{
								backgroundColor: offer.Status.toLowerCase() === "active" ? "#4caf50" : "#e0e0e0",
								color: offer.Status.toLowerCase() === "active" ? "white" : "#666",
								height: "20px",
								fontSize: "0.7rem",
							}}
						/>
					</Box>
					<Typography variant="body2" color="textSecondary">
						Offer ID: {offer.OfferID}
					</Typography>
				</Box>
				{offer.Status.toLowerCase() === "active" && (
					<OfferActionsMenu
						customerID={customerID}
						offerID={offer.OfferID}
						onRevokeSuccess={onRevokeSuccess}
					/>
				)}
			</Box>

			{offer.CampaignID && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Campaign ID: {offer.CampaignID}
					</Typography>
				</Box>
			)}

			{offer.Notes && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Notes: {offer.Notes}
					</Typography>
				</Box>
			)}

			<Divider style={{margin: "16px 0"}} />

			<Typography variant="subtitle2" gutterBottom>
				Options
			</Typography>
			<Box display="flex" flexDirection="column" style={{gap: "12px"}}>
				{sortedTiers.map((tier, index) => (
					<OfferTierCard key={tier.TierID} tier={tier} index={index} />
				))}
			</Box>

			<Box mt={2}>
				<Typography variant="caption" color="textSecondary">
					Created: {formatDate(offer.CreatedAt)}
					{offer.AgentEmail && ` by ${offer.AgentEmail}`}
				</Typography>
			</Box>
		</Paper>
	)
}

export {OfferCard}
